import { useCallback, useEffect } from "react";
import useSWR from "swr";

import type {
  SerializedVariant,
  UADiscreteControlFunction,
  UALADSFunctionalUnitFunctionSetInner,
} from "../../server/lads-api/clients/lcc-openapi";
import type { LightStatus } from "../api/laboperator.types";
import {
  callDeviceMethod,
  receiveLADSFunctionalUnit,
  receiveLADSFunctionalUnits,
  receiveLADSFunctionSet,
  setControlValue,
} from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  ladsWebSocketService,
  type UAFunctionValueMessage,
} from "../util/websocket-lads";
import type { OperationMode } from "../view-model";
import { useActiveRoom } from "./use-rooms";

// One controllable LADS function, flattened for the UI. Shared by the device
// control panel and the group control — the single shape all "write" flows use.
export type ControlFunction = {
  browseName: string;
  displayName: string;
  unitId: string;
  unitDisplayName: string;
  functionPath: string;
  type: string; // OPC-UA leaf type name (kept for display / debugging).
  typeHierarchy: string[]; // Full LADS type chain (LCU-851) — source of truth for classification.
  enumStrings?: string[]; // For discrete controls (On/Off, …)
  unit?: string; // For analog controls (°C, …)
  euRange?: { low: number; high: number };
  currentValue?: string | number;
  targetValue?: string | number;
  isUserWritable: boolean;
  isEnabled: boolean;
};

// The LADS base type every control function derives from. Classifying by the
// type hierarchy (LCU-851) instead of a substring on the leaf type name also
// catches vendor subtypes whose name lacks "Control" (e.g. PumpStageSelectorType,
// a subtype of AnalogControlFunctionType).
const BASE_CONTROL_FUNCTION_TYPE = "BaseControlFunctionType";
const ANALOG_CONTROL_FUNCTION_TYPE = "AnalogControlFunctionType";
const DISCRETE_CONTROL_FUNCTION_TYPE = "DiscreteControlFunctionType";
const TWO_STATE_CONTROL_FUNCTION_TYPE = "TwoStateDiscreteControlFunctionType";

function isControlFunction(
  func: UALADSFunctionalUnitFunctionSetInner
): func is UADiscreteControlFunction {
  return func.typeHierarchy?.includes(BASE_CONTROL_FUNCTION_TYPE) ?? false;
}

/** Numeric setpoint control (rendered as a number input). */
export function isAnalogControl(cf: ControlFunction): boolean {
  return cf.typeHierarchy.includes(ANALOG_CONTROL_FUNCTION_TYPE);
}

/** Enum-state control (button group / dropdown) — covers two- and multi-state
 *  discrete subtypes via the shared DiscreteControlFunctionType ancestor. */
export function isDiscreteControl(cf: ControlFunction): boolean {
  return cf.typeHierarchy.includes(DISCRETE_CONTROL_FUNCTION_TYPE);
}

/** Exactly two states (On/Off) — rendered as a small button row. */
export function isTwoStateControl(cf: ControlFunction): boolean {
  return cf.typeHierarchy.includes(TWO_STATE_CONTROL_FUNCTION_TYPE);
}

// OPC-UA scalar DataTypes that map to a numeric input; everything else is text.
const NUMERIC_DATATYPES = new Set([
  "Double",
  "Float",
  "Number",
  "Int16",
  "Int32",
  "Int64",
  "SByte",
  "UInt16",
  "UInt32",
  "UInt64",
  "Byte",
]);

/** Coerce a raw input string to the JS value the argument's DataType expects. */
export function coerceArgValue(dataType: string, raw: string): unknown {
  if (NUMERIC_DATATYPES.has(dataType)) return Number(raw);
  if (dataType === "Boolean") return raw === "true";
  return raw;
}

export function extractControlFunctions(
  functionalUnits: Array<{
    browseName?: string;
    displayName?: string;
    functionSet?: unknown[];
  }>,
  functionSetsMap: Map<string, UALADSFunctionalUnitFunctionSetInner[]>,
  deviceId: string
): ControlFunction[] {
  const controlFunctions: ControlFunction[] = [];

  for (const unit of functionalUnits) {
    const unitId = unit.browseName || "";
    // functionSet may come inline in the unit (present in the API response even
    // though the TS type omits it) or via the separately-fetched map.
    const functionSets: UALADSFunctionalUnitFunctionSetInner[] =
      unit.functionSet &&
      Array.isArray(unit.functionSet) &&
      unit.functionSet.length > 0
        ? (unit.functionSet as UALADSFunctionalUnitFunctionSetInner[])
        : functionSetsMap.get(unitId) || [];

    for (const func of functionSets) {
      if (!isControlFunction(func)) continue;

      const targetValue = func.targetValue;
      if (!targetValue) continue;

      const functionPath = `DeviceSet/${deviceId}/FunctionalUnitSet/${unitId}/FunctionSet/${func.browseName}`;
      const currentValue = func.currentValue?.value?.value;
      const targetValueValue = targetValue.value?.value;
      const enumStrings = targetValue.enumStrings || [];
      const targetValueAny = targetValue as unknown as Record<string, unknown>;

      // The TargetValue node carries two OPC-UA access levels:
      //  - accessLevel: the node's own capability. Read-only → not a control,
      //    skip it (belongs in the values view). Only skip when accessLevel is
      //    present and lacks write, so missing data doesn't hide everything.
      //  - userAccessLevel: the current session's effective right. A writable
      //    node the session can't write is still shown, just locked.
      const accessLevel = targetValueAny.accessLevel as string[] | undefined;
      if (accessLevel && !accessLevel.includes("currentWrite")) continue;

      const userAccessLevel = targetValueAny.userAccessLevel as
        | string[]
        | undefined;
      const isUserWritable = (userAccessLevel ?? accessLevel ?? []).includes(
        "currentWrite"
      );

      controlFunctions.push({
        browseName: func.browseName || "",
        displayName: func.displayName || func.browseName || "",
        unitId,
        unitDisplayName: unit.displayName || unitId,
        functionPath,
        type: func.type ?? "",
        typeHierarchy: func.typeHierarchy ?? [],
        enumStrings: enumStrings.length > 0 ? enumStrings : undefined,
        unit: targetValueAny.engineeringUnits as string | undefined,
        euRange: targetValueAny.euRange as
          | { low: number; high: number }
          | undefined,
        currentValue: currentValue,
        targetValue: targetValueValue,
        isUserWritable,
        isEnabled: func.isEnabled ?? true,
      });
    }
  }

  return controlFunctions;
}

// Resolve every unit's functionSet (inline, full-unit fetch, or separate
// endpoint) into one map. Shared by the hook and the imperative fetch.
async function buildFunctionSetsMap(
  deviceId: string,
  functionalUnits: Awaited<ReturnType<typeof receiveLADSFunctionalUnits>>,
  accessToken?: string
): Promise<Map<string, UALADSFunctionalUnitFunctionSetInner[]>> {
  const map = new Map<string, UALADSFunctionalUnitFunctionSetInner[]>();
  await Promise.all(
    functionalUnits.map(async (unit) => {
      const unitId = unit.browseName || "";
      if (!unitId) return;

      if (
        "functionSet" in unit &&
        Array.isArray(unit.functionSet) &&
        unit.functionSet.length > 0
      ) {
        map.set(
          unitId,
          unit.functionSet as UALADSFunctionalUnitFunctionSetInner[]
        );
        return;
      }

      try {
        const fullUnit = await receiveLADSFunctionalUnit(
          deviceId,
          unitId,
          accessToken
        );
        if (
          "functionSet" in fullUnit &&
          Array.isArray(fullUnit.functionSet) &&
          fullUnit.functionSet.length > 0
        ) {
          map.set(
            unitId,
            fullUnit.functionSet as UALADSFunctionalUnitFunctionSetInner[]
          );
        } else {
          const functionSets = await receiveLADSFunctionSet(
            deviceId,
            unitId,
            accessToken
          );
          map.set(
            unitId,
            functionSets as UALADSFunctionalUnitFunctionSetInner[]
          );
        }
      } catch {
        map.set(unitId, []);
      }
    })
  );
  return map;
}

/**
 * Imperative one-shot variant — for flows that write to several devices in
 * sequence (group control) where a hook per device isn't possible.
 */
export async function fetchControlFunctions(
  deviceId: string,
  accessToken?: string
): Promise<ControlFunction[]> {
  const functionalUnits = await receiveLADSFunctionalUnits(
    deviceId,
    accessToken
  );
  if (!functionalUnits || functionalUnits.length === 0) return [];
  const functionSetsMap = await buildFunctionSetsMap(
    deviceId,
    functionalUnits,
    accessToken
  );
  return extractControlFunctions(functionalUnits, functionSetsMap, deviceId);
}

/** Pick the OperationMode / Light control from a device's controls. */
export function findGroupControl(
  controls: ControlFunction[],
  controlType: "operation_status" | "light"
): ControlFunction | undefined {
  return controls.find(({ browseName }) =>
    controlType === "operation_status"
      ? browseName === "OperationMode" ||
        browseName.endsWith("_operation_status")
      : browseName === "Light" || browseName.endsWith("_light")
  );
}

// Legacy mode names → device EnumStrings labels: real LADS firmware labels its
// operation modes Off/Decreased/Normal/Increased.
const OPERATION_SYNONYMS: Record<string, string[]> = {
  off: ["off"],
  low: ["low", "decreased"],
  normal: ["normal"],
  high: ["high", "increased"],
};

/** Map a legacy mode name to the device's own EnumStrings label. */
export function resolveOperationLabel(
  control: ControlFunction,
  mode: string
): string {
  const synonyms = OPERATION_SYNONYMS[mode] ?? [mode];
  return (
    control.enumStrings?.find((label) =>
      synonyms.includes(label.toLowerCase())
    ) ?? mode
  );
}

// Reverse of OPERATION_SYNONYMS: device EnumStrings label (lowercased) → button
// mode. Lets the dashboard read a device's current OperationMode label back to
// off/low/normal/high.
const OPERATION_LABEL_TO_MODE: Record<string, OperationMode> =
  Object.fromEntries(
    Object.entries(OPERATION_SYNONYMS).flatMap(([mode, labels]) =>
      labels.map((label) => [label.toLowerCase(), mode as OperationMode])
    )
  );

// Map a control's current label ("Normal", "Off", …) to a button mode / light.
function modeFromLabel(
  label: string | number | undefined
): OperationMode | undefined {
  return label == null
    ? undefined
    : OPERATION_LABEL_TO_MODE[String(label).toLowerCase()];
}

function lightFromLabel(
  label: string | number | undefined
): LightStatus | undefined {
  if (label == null) return undefined;
  const value = String(label).toLowerCase();
  if (["on", "true", "1"].includes(value)) return 1;
  if (["off", "false", "0"].includes(value)) return 0;
  return undefined;
}

// All defined values equal → that value; empty or differing → null.
function sharedValue<T>(values: (T | undefined)[]): T | null {
  const defined = values.filter((v): v is T => v != null);
  return defined.length > 0 && defined.every((v) => v === defined[0])
    ? defined[0]
    : null;
}

export type GroupState = {
  activeMode: OperationMode | null;
  activeLight: LightStatus | null;
};

/**
 * Shared OperationMode / Light state across a device group (the dashboard's
 * workplace fumehoods). These controls live under the Fumehood FunctionalUnit,
 * NOT on the device-list object — so we fetch each device's controls (the same
 * path the group-control write uses) and pick OperationMode/Light by canonical
 * browsePath via findGroupControl. sharedValue yields the common value or null
 * when they differ, driving the active-button highlight + disable.
 */
export function useWorkplaceGroupState(deviceIds: string[]): GroupState {
  const accessToken = useAccessToken();
  const { activeRoom } = useActiveRoom();
  const roomId = activeRoom?.id;

  // Stable primitive key so the SWR entry and the WS effect don't churn on the
  // fresh array identity the caller produces each render.
  const deviceIdsKey = [...deviceIds].sort().join(",");

  const { data, mutate } = useSWR(
    deviceIdsKey ? ["workplaceGroupState", deviceIdsKey] : null,
    async () => {
      const perDevice = await Promise.all(
        deviceIdsKey.split(",").map(async (deviceId) => {
          const controls = await fetchControlFunctions(
            deviceId,
            accessToken ?? undefined
          );
          return {
            mode: modeFromLabel(
              findGroupControl(controls, "operation_status")?.targetValue
            ),
            light: lightFromLabel(
              findGroupControl(controls, "light")?.targetValue
            ),
          };
        })
      );
      return {
        activeMode: sharedValue(perDevice.map((p) => p.mode)),
        activeLight: sharedValue(perDevice.map((p) => p.light)),
      };
    },
    { refreshInterval: 30_000, revalidateOnFocus: false }
  );

  // The backend emits the device-confirmed OperationMode/Light TargetValue over
  // the LADS WebSocket ~1-2s after a write (group control or any external
  // change). Re-read on those messages so the highlight updates without a manual
  // refresh — no guessed settle delay.
  useEffect(() => {
    if (!roomId || !deviceIdsKey) return;
    const ids = new Set(deviceIdsKey.split(","));
    ladsWebSocketService.subscribe(roomId);
    const unsubscribe = ladsWebSocketService.onValue(
      (message: UAFunctionValueMessage) => {
        if (!ids.has(message.deviceId)) return;
        const fn = message.functionName;
        const isGroupFunction =
          fn === "OperationMode" ||
          fn === "Light" ||
          fn.endsWith("_operation_status") ||
          fn.endsWith("_light");
        const variableType = message.variableType.toLowerCase();
        if (
          isGroupFunction &&
          (variableType === "targetvalue" || variableType === "currentvalue")
        ) {
          void mutate();
        }
      }
    );
    return () => {
      ladsWebSocketService.unsubscribe(roomId);
      unsubscribe();
    };
  }, [roomId, deviceIdsKey, mutate]);

  return data ?? { activeMode: null, activeLight: null };
}

/**
 * useControl — the single entry point for a device's control operations.
 *
 * Fetches the device's controllable functions and exposes the two write paths:
 *  - write(functionPath, value): set a control setpoint / enum selection
 *  - call(browsePath, inputArguments): invoke a device method
 *
 * Every frontend "write" goes through here, so auth and the API surface live in
 * one place.
 */
export function useControl(deviceId?: string) {
  const accessToken = useAccessToken();

  const { data, error, isLoading } = useSWR(
    deviceId ? ["controlFunctions", deviceId] : null,
    ([, id]) => fetchControlFunctions(id, accessToken ?? undefined),
    { revalidateOnFocus: false, refreshInterval: 30_000 }
  );

  const write = useCallback(
    (functionPath: string, value: string | number) =>
      setControlValue(functionPath, value, accessToken ?? undefined),
    [accessToken]
  );

  const call = useCallback(
    (browsePath: string, inputArguments: SerializedVariant[]) =>
      callDeviceMethod(browsePath, inputArguments, accessToken ?? undefined),
    [accessToken]
  );

  return { controls: data ?? [], write, call, isLoading, error };
}
